///////////////////////////////////////////////////////////////
///
///  GLOBAL STATE MANAGEMENT
///
///  To inspect the state of the application, use redux-devtools
///  extension.
///
///////////////////////////////////////////////////////////////

import create from "zustand";
import { devtools } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import { persist } from "zustand/middleware";

//TYPES
import {
  MeetingsStore,
  UtilityStore,
  AuthenticationStore,
  UserStore,
  MeetingStore,
  InMeetingStore,
  ContactsStore,
  contactsFilterType,
  ContainerStore,
} from "./types";
import {
  CognitoUser,
  CurrentUser,
  OtherUsers,
  VTMeetings,
  VideoClient,
  InitialsObject,
  ScreenShare,
} from "../types";
import { VisiWebRTC } from "av-sdk";

///////////////////////////////////////////////////////////////
///
/// CONFIGURATION
///
///////////////////////////////////////////////////////////////

// HIDE DEVTOOLS ACCESS IN PRODUCTION
const activeDevtools = (process.env.NODE_ENV === "production"
  ? (fn: any) => fn
  : devtools) as unknown as typeof devtools;

///////////////////////////////////////////////////////////////
///
///  MEETINGS  STATE STORE (CALENDAR)
///
///////////////////////////////////////////////////////////////
export const meetingsStore = create<MeetingsStore>()(
  activeDevtools((set, get) => ({
    // CONTROLS DATE POINTER
    globalDate: new Date(),
    setGlobalDate: (newDate: Date) => set({ globalDate: newDate }),

    //CONTROL DATE POINTER FOR CREATE AND EDIT MEETING
    dateForCalendar: new Date(),
    setDateForCalendar: (newDate: Date) => set({ dateForCalendar: newDate }),

    //CONTROL DATE POINTER FOR UNTIL DATE SELECTOR IN REPEAT MEETING
    dateForUntilCalendar: new Date(),
    setDateForUntilCalendar: (newDate: Date) =>
      set({ dateForUntilCalendar: newDate }),

    // CONTROLS MEETINGS VIEW STATE
    meetingsView: "daily",
    setMeetingsView: (newView: string) => set({ meetingsView: newView }),

    // FILTER APPLIED TO MEETING RESULTS
    meetingsFilter: "all",
    setMeetingsFilter: (newFilter: string) =>
      set({ meetingsFilter: newFilter }),

    // CONTROLS INVITE STATUS STATE CHANGE
    inviteStatusUpdate: false,
    setInviteStatusUpdate: (newStatus: boolean) =>
      set({ inviteStatusUpdate: newStatus }),

    // CONTROL MEETINGS LOADING STATE
    meetingsLoading: true,
    setMeetingsLoading: (newStatus: boolean) =>
      set({ meetingsLoading: newStatus }),

    // CONTROL MEETING UUID STATE
    meetingUUID: "",
    setMeetingUUID: (uuid: string) => set({ meetingUUID: uuid }),

    //CONTROLS MEETING DATE STATE
    meetingDate: "",
    setMeetingDate: (date: string) => set({ meetingDate: date }),
    //RESET ALL VALUES
    resetMeetingsStore: () => {
      set({
        meetingsView: "daily",
        globalDate: new Date(),
        meetingsFilter: "all",
        inviteStatusUpdate: false,
        meetingsLoading: true,
      });
    },
  }))
);

///////////////////////////////////////////////////////////////
///
///  INDIVIDUAL MEETING STATE STORE
///
///////////////////////////////////////////////////////////////

export const meetingStore = create<MeetingStore>()(
  activeDevtools((set, get) => ({
    // CONTROLS IN MEETING STATE
    isInMeeting: false,
    setIsInMeeting: (newState: boolean) => set({ isInMeeting: newState }),

    // CLIENT
    client: <VisiWebRTC>{},
    setClient: (newState: any) => set({ client: newState }),

    // LOCAL CLIENT STATE
    localClients: [],
    getLocalClients: () => get().localClients,
    setLocalClients: (newState: any) => {
      set({ localClients: newState });
    },
    addLocalClient: (client: VideoClient) => {
      set((prevState: MeetingStore) => ({
        localClients: [...prevState.localClients, client],
      }));
    },
    removeLocalClientById: (clientID: string) => {
      set((prevState: MeetingStore) => ({
        localClients: prevState.localClients.filter(
          (client: VideoClient) => client.id !== clientID
        ),
      }));
    },
    removeLocalClientByEmail: (email: string) => {
      set((prevState: MeetingStore) => ({
        localClients: prevState.localClients.filter(
          (client: VideoClient) => client.email !== email
        ),
      }));
    },

    // REMOTE CLIENT STATE (VIDEO)
    remoteClients: [],
    getRemoteClients: () => get().remoteClients,
    setRemoteClients: (newState: any) => set({ remoteClients: newState }),
    addRemoteClient: (client: VideoClient) =>
      set((prevState: MeetingStore) => ({
        remoteClients: [...prevState.remoteClients, client],
      })),

    removeRemoteClientById: (clientID: string) => {
      set((prevState: MeetingStore) => ({
        remoteClients: prevState.remoteClients.filter(
          (client: VideoClient) => client.id !== clientID
        ),
      }));
    },
    removeRemoteClientByEmail: (email: string) => {
      set((prevState: MeetingStore) => ({
        remoteClients: prevState.remoteClients.filter(
          (client: VideoClient) => client.email !== email
        ),
      }));
    },

    // REMOTE AUDIO CLIENT STATE
    remoteAudioClients: [],
    getRemoteAudioClients: () => get().remoteAudioClients,
    setRemoteAudioClients: (newState: any) =>
      set({ remoteAudioClients: newState }),
    addRemoteAudioClient: (client: {
      id: string;
      email: string;
      name: string;
    }) =>
      set((prevState: MeetingStore) => ({
        remoteAudioClients: [...prevState.remoteAudioClients, client],
      })),
    removeRemoteAudioClientById: (clientID: string) => {
      set((prevState: MeetingStore) => ({
        remoteAudioClients: prevState.remoteAudioClients.filter(
          (client: { id: string; email: string; name: string }) =>
            client.id !== clientID
        ),
      }));
    },

    //VIDEO SCREEN SHARES
    screenShareClients: [],
    setScreenShareClients: (newState: any) => {
      set({ screenShareClients: newState });
    },
    getScreenShareClients: () => get().screenShareClients,
    addScreenShareClient: (client: ScreenShare) =>
      set((prevState: MeetingStore) => ({
        screenShareClients: [...prevState.screenShareClients, client],
      })),
    removeScreenShareClientById: (clientID: string) => {
      set((prevState: MeetingStore) => ({
        screenShareClients: prevState.screenShareClients.filter(
          (client: ScreenShare) => client.id !== clientID
        ),
      }));
    },

    //INITIALS SCREEN SHARES LOCAL
    initialsScreenShares: [],
    setInitialsScreenShares: (newState: InitialsObject[]) => {
      set({ initialsScreenShares: newState });
    },
    getInitialsScreenShares: () => get().initialsScreenShares,
    addInitialsScreenShares: (client: InitialsObject) => {
      if (get().initialsScreenShares.indexOf(client) === -1) {
        set((prevState: MeetingStore) => ({
          initialsScreenShares: [...prevState.initialsScreenShares, client],
        }));
      }
    },
    removeInitialsScreenSharesByEmail: (email: string) => {
      set((prevState: MeetingStore) => ({
        initialsScreenShares: prevState.initialsScreenShares.filter(
          (client: InitialsObject) => client.email !== email
        ),
      }));
    },

    pinnedClients: [],
    setPinnedClients: (newState: VideoClient[] | ScreenShare[]) => {
      set({ pinnedClients: newState });
    },
    addPinnedClient: (client: VideoClient | ScreenShare) => {
      set((prevState: MeetingStore) => ({
        pinnedClients: [...prevState.pinnedClients, client],
      }));
    },
    removePinnedClient: (client: VideoClient | ScreenShare) => {
      set((prevState: MeetingStore) => ({
        pinnedClients: prevState.pinnedClients.filter(
          (pinned: VideoClient | ScreenShare) => pinned.id !== client.id
        ),
      }));
    },

    focusedClient: {} as VideoClient | ScreenShare,
    setFocusedClient: (newState: VideoClient | ScreenShare) => {
      set({ focusedClient: newState });
    },

    // PARTICIPANTS
    /* // Not sure we can do a "computed property" in Zustand like this. This might need to move into the Participants _component_ instead
    getparticipants: () => {
      const audioStreamIds = get().remoteAudioClients.map((s) => s.id);
      const videoStreamIds = get().remoteClients.map((s) => s.id);
      const allStreamIds = [...audioStreamIds, ...videoStreamIds];
      const uniqueIds = [...new Set(allStreamIds)];
      return uniqueIds.map((id) => ({
        id,
        audioStream: get().remoteAudioClients.find((s) => s.id === id),
        videoStreams: get().remoteClients.filter((s) => s.id === id),
      }));
    },
    */

    loudestClient: {} as VideoClient,
    setLoudestClient: (newState: VideoClient) => {
      set({ loudestClient: newState });
    },

    //Permissions for video // audio
    permissionGranted: true,
    setPermissionGranted: (newState: boolean) =>
      set({ permissionGranted: newState }),

    //CLIENT'S VIDEO STATE
    videoIsOn: false,
    setVideoIsOn: (newState: boolean) =>
      set({
        videoIsOn: newState,
      }),

    //CLIENT'S AUDIO STATE
    microphoneIsOn: false,
    setMicrophoneIsOn: (newState: boolean) =>
      set({
        microphoneIsOn: newState,
      }),

    selectedMeeting: <VTMeetings>{},
    setSelectedMeeting: (meeting: VTMeetings) =>
      set({
        selectedMeeting: meeting,
      }),

    preCallModalIsOpen: false,
    setPreCallModalIsOpen: (newState: boolean) =>
      set({ preCallModalIsOpen: newState }),

    //Video Devices
    videoDevices: [],
    setVideoDevices: (newState: any) => set({ videoDevices: newState }),
    addVideoDevice: (device: MediaDeviceInfo) => {
      set((prevState: MeetingStore) => ({
        videoDevices: [...prevState.videoDevices, device],
      }));
    },

    //Microphones
    microphones: [],
    setMicrophones: (newState: any) => set({ microphones: newState }),
    addMicrophone: (device: MediaDeviceInfo) => {
      set((prevState: MeetingStore) => ({
        microphones: [...prevState.microphones, device],
      }));
    },

    audioOutput: [],
    setAudioOutput: (newState: any) => set({ audioOutput: newState }),

    activeVideoStream: <MediaStream>{},
    setActiveVideoStream: (newState: MediaStream) =>
      set({ activeVideoStream: newState }),

    blurredStream: <MediaStream>{},
    setBlurredStream: (newState: MediaStream) =>
      set({ blurredStream: newState }),

    activeAudioStream: <MediaStream>{},
    setActiveAudioStream: (newState: MediaStream) => {
      set({ activeAudioStream: newState });
    },

    //@ts-ignore
    activeSpeakerStream: null,
    setActiveSpeakerStream: (newState: any) =>
      set({
        activeSpeakerStream: newState,
      }),

    //@ts-ignore
    selectedVideoStream: "",
    setSelectedVideoStream: (newState: string) =>
      set({
        selectedVideoStream: newState,
      }),

    //@ts-ignore
    selectedMicrophoneStream: "",
    setSelectedMicrophoneStream: (newState: string) =>
      set({
        selectedMicrophoneStream: newState,
      }),

    //@ts-ignore
    selectedSpeakerStream: "",
    setSelectedSpeakerStream: (newState: string) =>
      set({ selectedSpeakerStream: newState }),

    volume: 0.5,
    setVolume: (newState: number) =>
      set({
        volume: newState,
      }),

    fitToFrame: true,
    setFitToFrame: (newState: boolean) => {
      set({ fitToFrame: newState });
    },

    blurEffectOn: false,
    setBlurEffectOn: (newState: boolean) => {
      set({ blurEffectOn: newState });
    },

    sidebarOpen: false,
    setSidebarOpen: (newState: boolean) => {
      set({ sidebarOpen: newState });
    },

    sideBarState: "",
    setSidebarState: (newState: string) => {
      set({ sideBarState: newState });
    },

    //view for the precall journey
    view: "landingPage",
    setView: (newState: string) => set({ view: newState }),

    screenShareEnded: false,
    setScreenShareEnded: (newState: boolean) =>
      set({ screenShareEnded: newState }),

    numberOfScreenShares: 0,
    setNumberOfScreenShares: (newState: number) =>
      set({ numberOfScreenShares: newState }),

    screenOrientation: "portrait",
    setScreenOrientation: (newState: string) =>
      set({
        screenOrientation: newState,
      }),
  }))
);

///////////////////////////////////////////////////////////////
///
///  CONTACTS STATE STORE
///
///////////////////////////////////////////////////////////////
export const contactsStore = create<ContactsStore>()(
  activeDevtools((set) => ({
    tab: 0,
    setTab: (tabindex: number) => set({ tab: tabindex }),

    newContactDialogOpen: false,
    setNewContactDialogOpen: (state: boolean) =>
      set({ newContactDialogOpen: state }),

    newGroupDialogOpen: false,
    setNewGroupDialogOpen: (state: boolean) =>
      set({ newGroupDialogOpen: state }),

    currentSelectedUser: "",
    setCurrentSelectedUser: (userID: string) =>
      set({ currentSelectedUser: userID }),

    // contactsAscending: true,
    // setContactsAscending: (state: boolean) => set({ contactsAscending: state }),
    contactsFilter: "alphabetical",
    setContactsFilter: (state: contactsFilterType) =>
      set({ contactsFilter: state }),

    removeContactDialogOpen: false,
    setRemoveContactDialogOpen: (state: boolean) =>
      set({ removeContactDialogOpen: state }),
  }))
);

///////////////////////////////////////////////////////////////
///
///  USER STATE STORE
///
///////////////////////////////////////////////////////////////

export const userStore = create<UserStore>()(
  activeDevtools((set, get) => ({
    user: <CurrentUser>{},
    setUser: (newState: CurrentUser) => set({ user: newState }),
    getUser: () => get().user,

    accountUsers: [],
    setAccountUsers: (newState: OtherUsers[]) => {
      set({ accountUsers: newState });
    },

    awsAmplifyUser: <CognitoUser>{},
    setAwsAmplifyUser: (newState: CognitoUser) => {
      set({ awsAmplifyUser: newState });
    },

    profileOpen: false,
    setProfileOpen: (newState: boolean) => set({ profileOpen: newState }),

    showStatusCard: false,
    setShowStatusCard: (newState: boolean) => set({ showStatusCard: newState }),

    selectedCustomStatus: "0",
    setSelectedCustomStatus: (newState: string) =>
      set({ selectedCustomStatus: newState }),
  }))
);
///////////////////////////////////////////////////////////////
///
///  AUTHENTICATION STATE STORE
///
///////////////////////////////////////////////////////////////

export const authStore = create<AuthenticationStore>()(
  activeDevtools((set, get) => ({
    // CONTROLS AUTHENTICATION STATE
    isSignedIn: false,
    setIsSignedIn: (newState: boolean) => set({ isSignedIn: newState }),

    // MFA ACCOUNT REQUIRED
    isMfaRequired: false,
    setIsMfaRequired: (newState: boolean) => set({ isMfaRequired: newState }),

    // CONTROLS SESSION STATE
    validSession: false,
    setValidSession: (newState: boolean) => set({ validSession: newState }),

    //BOOLEAN STATE TO SEE IF USER IS GUEST.
    isGuest: false,
    setIsGuest: (newState: boolean) => set({ isGuest: newState }),

    // AUTO GENERATED GUEST ID
    guestID: "",
    setGuestID: (guestID: string) => set({ guestID }),

    // MJWT
    mjwt: "",
    setMJWT: (mjwt: string) => set({ mjwt }),

    mjwtObject: {},
    setMjwtObject: (mjwtObject: any) => set({ mjwtObject }),

    guestName: "",
    setGuestName: (guestName: string) => set({ guestName }),

    config: {},
    setConfig: (newState: any) => set({ config: newState }),
  }))
);

///////////////////////////////////////////////////////////////
///
///  UTILITY STATE STORE
///  encorporates persistent storage in localStorage
///
///////////////////////////////////////////////////////////////

export const utilityStore = create<UtilityStore>()(
  subscribeWithSelector(
    activeDevtools(
      persist(
        (set, get) => ({
          // CONTROLS NAVBAR STATE
          navbarOpen: false,
          setNavbarOpen: (newState: boolean) => set({ navbarOpen: newState }),

          // CONTROLS DELETE MODAL
          deleteModalOpen: false,
          setDeleteModalOpen: (newState: boolean) =>
            set({ deleteModalOpen: newState }),

          deleteType: "",
          setDeleteType: (newState: string) => set({ deleteType: newState }),

          // CONTROLS SETTINGS DIALOG STATE
          settingsDialogOpen: false,
          setSettingsDialogOpen: (newState: boolean) =>
            set({ settingsDialogOpen: newState }),

          // REMEMBERS STATE OF KEEP ME SIGNED IN
          keepMeSignedInToggle: false,
          setKeepMeSignedInToggle: (newState: boolean) =>
            set({ keepMeSignedInToggle: newState }),

          // TOGGLE SIGN OUT MODAL
          signOutModalOpen: false,
          setSignOutModalOpen: (newState: boolean) =>
            set({ signOutModalOpen: newState }),
        }),

        {
          name: "setting-storage",
          getStorage: () => localStorage,
          // (optional) by default, 'localStorage' is used
        }
      )
    )
  )
);

///////////////////////////////////////////////////////////////
///
///  CONTAINER STORE
///
///
///////////////////////////////////////////////////////////////
export const containerStore = create<ContainerStore>()(
  activeDevtools((set, get) => ({
    containerWidth: 0,
    setContainerWidth: (newState: number) => set({ containerWidth: newState }),

    containerHeight: 0,
    setContainerHeight: (newState: number) =>
      set({ containerHeight: newState }),

    videoHeight: 0,
    setVideoHeight: (newState: number) => set({ videoHeight: newState }),

    videoWidth: 0,
    setVideoWidth: (newState: number) => set({ videoWidth: newState }),

    screenShareHeight: 0,
    setScreenShareHeight: (newState: number) =>
      set({ screenShareHeight: newState }),
    screenShareWidth: 0,
    setScreenShareWidth: (newState: number) =>
      set({ screenShareWidth: newState }),
  }))
);
